/**
 * Flow Visualization Plugin
 *
 * Provides advanced alluvial-style Sankey diagrams for visualizing
 * valuation component breakdowns and flows.
 *
 * Features:
 * - Alluvial Sankey diagrams with Apache ECharts
 * - Proportional height accumulation
 * - Dark mode support
 * - Configurable colors, layout, and display options
 * - No external API dependencies
 */

import { Plugin, PluginContext } from '@/lib/platform/types/plugins';
import { manifest } from './manifest';
import { defaultConfig } from './config';
import { CashFlowVisualizationConfig } from '@/lib/platform/types/cash-flow';

import { pluginLogger } from '@/lib/platform/utils/logger';

export const cashFlowVisualizationPlugin: Plugin<CashFlowVisualizationConfig> = {
  manifest,

  capabilities: {
    requiredEndpoints: [], // No API calls needed - renders from valuation data
    requiredPermissions: ['read:valuations'],

    features: {
      reports: false,
      dashboard: false,
      valuations: true, // Shows on valuation details page
      analytics: false,
      exports: false,
    },

    resourceLimits: {
      maxExecutionTimeMs: 1000, // Client-side rendering
    },
  },

  lifecycle: {
    async onInstall() {
      pluginLogger.log('[Flow Visualization] Plugin installed');
    },

    async onUninstall() {
      pluginLogger.log('[Flow Visualization] Plugin uninstalled');
    },

    async onEnable() {
      pluginLogger.log('[Flow Visualization] Plugin enabled');
    },

    async onDisable() {
      pluginLogger.log('[Flow Visualization] Plugin disabled');
    },

    async healthCheck() {
      // Simple health check - this plugin doesn't depend on external services
      return {
        status: 'healthy',
        message: 'Flow Visualization is ready',
        lastCheck: new Date(),
      };
    },
  },

  enabled: false,
  currentConfig: undefined,
  context: undefined,

  settings: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: null as any, // Settings component will be added in Phase 5
    schema: {
      type: 'object',
      properties: {
        chartType: {
          type: 'string',
          enum: ['sankey', 'waterfall', 'custom'],
        },
        height: {
          type: 'number',
        },
        colorScheme: {
          type: 'string',
          enum: ['default', 'blue', 'green', 'purple', 'custom'],
        },
        showLegend: {
          type: 'boolean',
        },
        showTooltips: {
          type: 'boolean',
        },
        showLabels: {
          type: 'boolean',
        },
        position: {
          type: 'string',
          enum: ['before-sales', 'after-sales', 'top', 'bottom'],
        },
        animationDuration: {
          type: 'number',
        },
        enableInteractivity: {
          type: 'boolean',
        },
      },
      required: ['chartType', 'height'],
    },
    defaultConfig,
  },

  async initialize(context: PluginContext) {
    cashFlowVisualizationPlugin.context = context;

    // Load config from storage
    const storedConfig = await context.storage.getAll<CashFlowVisualizationConfig>();
    cashFlowVisualizationPlugin.currentConfig = storedConfig || defaultConfig;

    context.logger.info('Flow Visualization plugin initialized');
  },

  async cleanup() {
    if (cashFlowVisualizationPlugin.context) {
      cashFlowVisualizationPlugin.context.logger.info('Flow Visualization plugin cleaning up');
    }
    cashFlowVisualizationPlugin.context = undefined;
  },
};

export default cashFlowVisualizationPlugin;
